/**
 * V7's "What is it?" and the questions after it (docs/superpowers/specs/2026-10-16-v7-build.md).
 *
 * One definition per type of app — the four the ads sell (Scheduler, Golf tracker, RSVP) and Other —
 * read by both groups of the `v7_flow` test (analytics/experiment.ts):
 *
 *   a: every question of the type on ONE screen (`a`), then the build starts by itself;
 *   b: the questions one per screen (`b`), then the plan's own screens and Build.
 *
 * The answers become plain lines under their sentence ("Track only: …", "Who uses it: …"), which is
 * what is planned and built: the model reads the prompt, so the prompt is where a choice has to be.
 * Pure: no React, no storage, so the screens, the tests and the funnel's allowlist share it.
 */
export type AppType = 'scheduler' | 'golf' | 'rsvp' | 'other';
export const APP_TYPES: AppType[] = ['scheduler', 'golf', 'rsvp', 'other'];

export interface Option {
  value: string;
  label: string;
  /** A second line under a radio's label. */
  sub?: string;
  /** The words used in the prompt, when the label's are not right there ("Coming?"). */
  says?: string;
}

interface Base {
  id: string;
  /** The question, as asked on screen. */
  label: string;
}
export type Question =
  | (Base & { kind: 'chips'; options: Option[]; defaults: string[]; max?: number; line: (picked: Option[]) => string | null })
  | (Base & { kind: 'checks'; options: Option[]; defaults: string[]; max?: number; line: (picked: Option[]) => string | null })
  | (Base & { kind: 'choice'; options: Option[]; default?: string; line: (picked: Option | null) => string | null })
  | (Base & { kind: 'radio'; options: Option[]; default: string; line: (picked: Option) => string })
  | (Base & { kind: 'text'; placeholder: string; optional: boolean; line: (said: string) => string | null })
  | (Base & { kind: 'toggle'; default: boolean; line: (on: boolean) => string | null })
  | (Base & { kind: 'date'; line: (date: string) => string | null });

/** One of variant b's screens: its title, a line under it, and the questions on it. */
export interface Step {
  id: string;
  title: string;
  sub?: string;
  questions: Question[];
}

export interface TypeDef {
  type: AppType;
  /** On the toggle. */
  label: string;
  /** The sentence a switch to this type puts in the field (Other: none, a placeholder instead). */
  example: string;
  placeholder: string;
  /** "Build my tracker": variant a's button. */
  build: string;
  /** What it is called in a sentence: "keep your tracker". */
  thing: string;
  a: Question[];
  b: Step[];
}

export type Answer = string | string[] | boolean;
export type Answers = Record<string, Answer>;

const listOf = (picked: Option[]): string => picked.map((o) => o.says ?? o.label).join(', ');

/** Who uses it: the same three answers for a golf tracker and for anything else, worded for each. */
function whoUses(defaultTo: 'just_me' | 'anyone', mine: string, ours: string): Question {
  return {
    id: 'who',
    kind: 'radio',
    label: 'Who uses it?',
    options: [
      { value: 'just_me', label: 'Just me', sub: 'Private. You sign in.' },
      { value: 'friends', label: 'Me and friends I share it with', sub: 'They sign in.' },
      { value: 'anyone', label: 'Anyone with the link', sub: 'No sign-up.' },
    ],
    default: defaultTo,
    line: (o) =>
      o.value === 'just_me'
        ? `Who uses it: just me; I sign in to keep ${mine}.`
        : o.value === 'friends'
          ? `Who uses it: me and friends I share it with; each of us signs in and keeps ${ours}.`
          : 'Who uses it: anyone with the link, with no sign-up.',
  };
}

const golfWho = whoUses('just_me', 'my rounds', 'our own rounds');
// Variant b's version says more under each answer (V7-B/06-who).
const golfWhoB: Question = {
  ...golfWho,
  kind: 'radio',
  options: [
    { value: 'just_me', label: 'Just me', sub: 'Private. You sign in; only you see it.' },
    { value: 'friends', label: 'Friends I share it with (they sign in)', sub: 'Each friend keeps their own rounds.' },
    { value: 'anyone', label: 'Anyone with the link (no sign-up)', sub: 'Whoever opens it can log a round.' },
  ],
  default: 'just_me',
  line: (golfWho as Extract<Question, { kind: 'radio' }>).line,
};

const homeCourse: Question = {
  id: 'course',
  kind: 'text',
  label: 'Home course (optional)',
  placeholder: 'e.g. Ashridge Park',
  optional: true,
  line: (said) => (said ? `Home course: ${said}.` : null),
};

const GOLF_TRACK_A: Question = {
  id: 'track',
  kind: 'chips',
  label: 'What should it track?',
  options: [
    { value: 'score', label: 'Score per hole' },
    { value: 'clubs', label: 'Clubs & distances' },
    { value: 'fairways', label: 'Fairways & greens' },
    { value: 'putts', label: 'Putts' },
    { value: 'handicap', label: 'Handicap trend' },
    { value: 'practice', label: 'Practice' },
  ],
  defaults: ['score', 'clubs', 'fairways'],
  line: (picked) => (picked.length ? `Track only: ${listOf(picked)}.` : null),
};

const GOLF_TRACK_B: Question = {
  id: 'track',
  kind: 'checks',
  label: 'Pick up to 5.',
  options: [
    { value: 'scoring', label: 'Scoring average and trend' },
    { value: 'by_par', label: 'Score by par (3/4/5)' },
    { value: 'distances', label: 'Club distances' },
    { value: 'accuracy', label: 'Club accuracy' },
    { value: 'fairways', label: 'Fairways hit' },
    { value: 'gir', label: 'Greens in regulation' },
    { value: 'putts', label: 'Putts per round' },
    { value: 'up_downs', label: 'Up-and-downs' },
    { value: 'penalties', label: 'Penalties' },
  ],
  defaults: ['scoring', 'distances', 'putts'],
  max: 5,
  line: (picked) => (picked.length ? `Track only: ${listOf(picked)}.` : null),
};

const GOLF_GAME: Question[] = [
  {
    id: 'score',
    kind: 'choice',
    label: 'Your typical score',
    options: [
      { value: 'under_80', label: 'Under 80' },
      { value: '80s', label: '80–89' },
      { value: '90s', label: '90–99' },
      { value: '100_plus', label: '100+' },
    ],
    line: (o) => (o ? `My typical score: ${o.label}.` : null),
  },
  {
    id: 'often',
    kind: 'choice',
    label: 'How often you play',
    options: [
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly', label: 'A few times a month' },
      { value: 'sometimes', label: 'Now and then' },
    ],
    line: (o) => (o ? `I play ${o.label.toLowerCase()}.` : null),
  },
  { ...homeCourse, label: 'Home course' } as Question,
];

const GOLF_LOG: Question = {
  id: 'log',
  kind: 'radio',
  label: 'How do you log a round?',
  options: [
    { value: 'hole_by_hole', label: 'Hole by hole, during the round', sub: 'Tap in each hole on your phone as you play.' },
    { value: 'scorecard', label: 'From the scorecard, after (photo)', sub: 'Take a photo of your card and check the numbers.' },
    { value: 'both', label: 'Both', sub: 'Log as you go, or add a round later from the card.' },
  ],
  default: 'hole_by_hole',
  line: (o) =>
    o.value === 'hole_by_hole'
      ? 'Logging: hole by hole, during the round.'
      : o.value === 'scorecard'
        ? 'Logging: after the round, from a photo of the scorecard.'
        : 'Logging: hole by hole during the round, or later from a photo of the scorecard.',
};

const SHIFT_NEEDS: Question = {
  id: 'shift',
  kind: 'chips',
  label: 'What does each shift need?',
  options: [
    { value: 'time', label: 'Time', says: 'time' },
    { value: 'role', label: 'Role', says: 'role' },
    { value: 'people', label: 'People needed', says: 'how many people it needs' },
    { value: 'notes', label: 'Notes', says: 'notes' },
  ],
  defaults: ['time', 'role', 'people'],
  line: (picked) => (picked.length ? `Each shift shows its ${listOf(picked)}.` : null),
};

const SIGN_UP: Question = {
  id: 'signup',
  kind: 'radio',
  label: 'How do people sign up?',
  options: [
    { value: 'first_name', label: 'First name only' },
    { value: 'name_email', label: 'Name and email' },
    { value: 'account', label: 'With an account' },
  ],
  default: 'first_name',
  line: (o) =>
    o.value === 'first_name'
      ? 'Sign-up: first name only.'
      : o.value === 'name_email'
        ? 'Sign-up: name and email.'
        : 'Sign-up: with an account (people sign in).',
};

const REMINDERS: Question = {
  id: 'reminders',
  kind: 'toggle',
  label: 'Email reminders the day before',
  default: true,
  line: (on) => (on ? 'Email reminders the day before.' : 'No reminder emails.'),
};

const GUEST_ANSWERS: Question = {
  id: 'answers',
  kind: 'chips',
  label: 'What should guests answer?',
  options: [
    { value: 'coming', label: 'Coming?', says: 'whether they are coming' },
    { value: 'meal', label: 'Meal choice', says: 'their meal choice' },
    { value: 'plus_one', label: 'Plus-one', says: 'a plus-one' },
    { value: 'dietary', label: 'Dietary needs', says: 'dietary needs' },
  ],
  defaults: ['coming', 'meal', 'plus_one'],
  line: (picked) => (picked.length ? `Guests answer: ${listOf(picked)}.` : null),
};

const WHO_REPLIES: Question = {
  id: 'reply',
  kind: 'radio',
  label: 'Who can reply?',
  options: [
    { value: 'anyone', label: 'Anyone with the link' },
    { value: 'invited', label: 'Only invited guests', sub: 'They sign in.' },
  ],
  default: 'anyone',
  line: (o) => (o.value === 'anyone' ? 'Who can reply: anyone with the link, no sign-in.' : 'Who can reply: only invited guests, who sign in.'),
};

const REPLY_BY: Question = {
  id: 'reply_by',
  kind: 'date',
  label: 'Reply by',
  line: (date) => (date ? `Reply by: ${spokenDate(date)}.` : null),
};

const TRACKS: Question = {
  id: 'tracks',
  kind: 'text',
  label: 'What must it keep track of?',
  placeholder: 'e.g. who has paid, and how much',
  optional: true,
  line: (said) => (said ? `It keeps track of: ${said}.` : null),
};

export const TYPES: Record<AppType, TypeDef> = {
  scheduler: {
    type: 'scheduler',
    label: 'Scheduler',
    example: 'Staff schedule for my café: open, mid and close shifts, two people each.',
    placeholder: 'Describe your schedule…',
    build: 'Build my schedule',
    thing: 'schedule',
    a: [SHIFT_NEEDS, SIGN_UP, REMINDERS],
    b: [
      { id: 'shift', title: 'What does each shift need?', sub: 'The details on every shift.', questions: [{ ...SHIFT_NEEDS, label: 'Pick what to show.' } as Question] },
      { id: 'signup', title: 'How do people sign up?', sub: 'This decides whether people make an account.', questions: [{ ...SIGN_UP, label: '' } as Question] },
      { id: 'reminders', title: 'Reminders', sub: 'A nudge so nobody forgets their shift.', questions: [REMINDERS] },
    ],
  },
  golf: {
    type: 'golf',
    label: 'Golf tracker',
    // The golf ad's own sentence (journey/demos.ts), so a switch back to Golf finds it again.
    example: 'A golf tracker where I log each round with my score on every hole and the club I used for each stroke, and see over time how far and how well I hit each club, with charts.',
    placeholder: 'Describe your golf tracker…',
    build: 'Build my tracker',
    thing: 'tracker',
    a: [GOLF_TRACK_A, golfWho, homeCourse],
    b: [
      { id: 'track', title: 'What do you want to track?', questions: [GOLF_TRACK_B] },
      { id: 'game', title: 'About your game', sub: 'So your tracker starts out right for you.', questions: GOLF_GAME },
      { id: 'log', title: 'How do you log a round?', sub: 'We’ll build the round screen around it.', questions: [{ ...GOLF_LOG, label: '' } as Question] },
      { id: 'who', title: 'Who uses it?', sub: 'This decides whether people sign up.', questions: [{ ...golfWhoB, label: '' } as Question] },
    ],
  },
  rsvp: {
    type: 'rsvp',
    label: 'RSVP',
    example: 'An RSVP for our wedding on June 12: who’s coming, their meal and plus-ones.',
    placeholder: 'Describe your event…',
    build: 'Build my RSVP',
    thing: 'RSVP',
    a: [GUEST_ANSWERS, WHO_REPLIES, REPLY_BY],
    b: [
      { id: 'answers', title: 'What should guests answer?', sub: 'Every reply asks these.', questions: [{ ...GUEST_ANSWERS, label: 'Pick what to ask.' } as Question] },
      { id: 'reply', title: 'Who can reply?', sub: 'This decides whether guests sign in.', questions: [{ ...WHO_REPLIES, label: '' } as Question] },
      { id: 'reply_by', title: 'Reply by', sub: 'When replies close (optional).', questions: [{ ...REPLY_BY, label: 'Date' } as Question] },
    ],
  },
  other: {
    type: 'other',
    label: 'Other',
    example: '',
    placeholder: 'Describe what you want to make…',
    build: 'Build it',
    thing: 'app',
    a: [whoUses('anyone', 'it private', 'our own entries'), TRACKS],
    b: [
      { id: 'who', title: 'Who uses it?', sub: 'This decides whether people sign up.', questions: [{ ...whoUses('anyone', 'it private', 'our own entries'), label: '' } as Question] },
      { id: 'tracks', title: 'What must it keep track of?', sub: 'In a few words.', questions: [{ ...TRACKS, label: '' } as Question] },
    ],
  },
};

/** Every step a question screen can report (`questions_viewed`'s `step`): b's screens, and a's one. */
export const QUESTION_STEPS: string[] = ['all', ...new Set(APP_TYPES.flatMap((t) => TYPES[t].b.map((s) => s.id)))];

/** The questions of a type in the group's order (a's screen, or b's screens one after another). */
export function questionsFor(type: AppType, group: 'a' | 'b'): Question[] {
  return group === 'a' ? TYPES[type].a : TYPES[type].b.flatMap((s) => s.questions);
}

/** Each question's starting answer: the ticks and choices the mockups show. */
export function defaultAnswers(type: AppType, group: 'a' | 'b'): Answers {
  const out: Answers = {};
  for (const q of questionsFor(type, group)) {
    if (q.kind === 'chips' || q.kind === 'checks') out[q.id] = [...q.defaults];
    else if (q.kind === 'radio') out[q.id] = q.default;
    else if (q.kind === 'choice') out[q.id] = q.default ?? '';
    else if (q.kind === 'toggle') out[q.id] = q.default;
    else out[q.id] = '';
  }
  return out;
}

/** An answer typed into a field: one line, short, and never a second sentence's worth of punctuation. */
const MAX_TYPED = 120;
function typed(raw: unknown): string {
  return String(raw ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_TYPED).trim().replace(/[.\s]+$/, '');
}

/** The plain lines the answers make, in the order they were asked. */
export function answerLines(type: AppType, group: 'a' | 'b', answers: Answers): string[] {
  const lines: string[] = [];
  for (const q of questionsFor(type, group)) {
    const a = answers[q.id];
    let line: string | null = null;
    if (q.kind === 'chips' || q.kind === 'checks') {
      const chosen = Array.isArray(a) ? a : q.defaults;
      line = q.line(q.options.filter((o) => chosen.includes(o.value)));
    } else if (q.kind === 'radio') {
      line = q.line(q.options.find((o) => o.value === a) ?? q.options.find((o) => o.value === q.default)!);
    } else if (q.kind === 'choice') {
      line = q.line(q.options.find((o) => o.value === a) ?? null);
    } else if (q.kind === 'toggle') {
      line = q.line(typeof a === 'boolean' ? a : q.default);
    } else if (q.kind === 'date') {
      line = q.line(/^\d{4}-\d{2}-\d{2}$/.test(String(a ?? '')) ? String(a) : '');
    } else {
      line = q.line(typed(a));
    }
    if (line) lines.push(line);
  }
  return lines;
}

/** What is planned and built: their sentence, then one plain line per answer. */
export function composePrompt(sentence: string, type: AppType, group: 'a' | 'b', answers: Answers): string {
  const said = sentence.trim();
  const lines = answerLines(type, group, answers);
  return [said, ...lines].filter(Boolean).join('\n');
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "2027-05-15" as "May 15, 2027" — or "May 15" in the current year. */
export function spokenDate(iso: string, now: Date = new Date()): string {
  const [y, m, d] = iso.split('-').map(Number);
  const day = `${MONTHS[m - 1]} ${d}`;
  return y === now.getFullYear() ? day : `${day}, ${y}`;
}
